import React, { useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../utils/ThemeSwitcher';
import './AuroraBackground.css';

/**
 * Aurora Background — DataQuad Edition
 *
 * Palette is now derived from the brand:
 *   - amber/gold       (primary accent, "analyse" rail, ring around 86)
 *   - teal/cyan        (secondary accent, "click to expand" pill)
 *   - emerald          (completion, healthy-state indicators)
 *   - deep navy/slate  (chrome — anchors the dark mode without going purple)
 *
 * Dark mode is intentionally darker and lower-saturation than the previous
 * royal-purple/magenta blend so the aurora reads as DataQuad ambient light,
 * not generic SaaS gradient.
 *
 * Light mode keeps soft, airy washes of the same hues so the palette stays
 * consistent across themes.
 */
const AuroraBackground = ({
  blobCount = 6,
  animationSpeed = 0.3,
  blurAmount = 120,
  saturation = 65,
  baseOpacity = 0.6,
  colorScheme = 'default',
  className = '',
  style = {}
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const blobsRef = useRef([]);
  const timeRef = useRef(0);
  const { isDarkMode } = useTheme();

  // ---------------------------------------------------------------------------
  // Brand-aligned palettes
  // ---------------------------------------------------------------------------
  const getColorPalette = useCallback(() => {
    const palettes = {
      // ─────────────────────────────────────────────────────────────────────
      // DEFAULT: DataQuad signature — amber + teal + emerald on navy
      // No purple, no magenta. Hues stay within the brand's warm/cool axis.
      // ─────────────────────────────────────────────────────────────────────
      default: isDarkMode ? [
        // Amber/gold leads. Greens minimized — pulled toward teal-blue and
        // anchored by navy/black so the dark mode reads brand-warm + cool,
        // not "aurora green".
        { h:  35, s: saturation - 5,  l: 38 },   // Amber / DataQuad gold (lead)
        { h:  25, s: saturation - 8,  l: 34 },   // Warm amber-orange (reinforce)
        { h: 205, s: saturation - 5,  l: 26 },   // Cool steel-blue
        { h: 220, s: saturation - 10, l: 18 },   // Deep navy / near-black anchor
        { h: 195, s: saturation - 12, l: 30 },   // Muted teal-blue (was emerald)
        { h: 215, s: saturation - 15, l: 14 },   // Black-blue depth
      ] : [
        { h:  35, s: saturation - 25, l: 88 },   // Soft amber wash (lead)
        { h:  25, s: saturation - 26, l: 91 },   // Cream-amber
        { h: 205, s: saturation - 30, l: 93 },   // Whisper steel-blue
        { h: 220, s: saturation - 32, l: 94 },   // Pale navy hint
        { h: 195, s: saturation - 32, l: 92 },   // Cool teal-blue (was mint)
        { h: 215, s: saturation - 35, l: 95 },   // Soft sky-navy
      ],

      // Ocean — kept (deep sea palette)
      ocean: isDarkMode ? [
        { h: 200, s: saturation + 15, l: 30 },
        { h: 185, s: saturation + 10, l: 36 },
        { h: 220, s: saturation + 5,  l: 32 },
        { h: 170, s: saturation,      l: 38 },
        { h: 195, s: saturation + 5,  l: 42 },
        { h: 215, s: saturation - 5,  l: 26 },
      ] : [
        { h: 195, s: saturation - 15, l: 88 },
        { h: 180, s: saturation - 20, l: 85 },
        { h: 210, s: saturation - 25, l: 90 },
        { h: 165, s: saturation - 30, l: 87 },
        { h: 200, s: saturation - 18, l: 82 },
        { h: 220, s: saturation - 28, l: 92 },
      ],

      // ─────────────────────────────────────────────────────────────────────
      // AURORA: re-tuned away from purple/pink. Now a true DataQuad aurora —
      // amber + teal + emerald shimmer over navy, evoking the brand's
      // "data flow" rather than generic northern lights.
      // ─────────────────────────────────────────────────────────────────────
      aurora: isDarkMode ? [
        { h: 165, s: saturation + 15, l: 38 },   // Aurora emerald
        { h:  35, s: saturation + 10, l: 42 },   // Amber surge
        { h: 195, s: saturation + 10, l: 36 },   // Cool teal
        { h: 145, s: saturation,      l: 40 },   // Bright green flash
        { h:  45, s: saturation - 5,  l: 44 },   // Honey light
        { h: 210, s: saturation + 5,  l: 32 },   // Steel anchor
      ] : [
        { h: 165, s: saturation - 20, l: 88 },
        { h:  35, s: saturation - 22, l: 91 },
        { h: 195, s: saturation - 25, l: 89 },
        { h: 145, s: saturation - 28, l: 90 },
        { h:  45, s: saturation - 26, l: 92 },
        { h: 200, s: saturation - 24, l: 87 },
      ],

      // Sunset — warm dusk (kept; useful as alternate)
      sunset: isDarkMode ? [
        { h:  30, s: saturation + 15, l: 40 },
        { h:  15, s: saturation + 10, l: 38 },
        { h:  45, s: saturation + 5,  l: 42 },
        { h:   5, s: saturation,      l: 35 },
        { h:  25, s: saturation + 12, l: 44 },
        { h: 200, s: saturation - 10, l: 28 },   // cool counter-anchor
      ] : [
        { h:  35, s: saturation - 20, l: 90 },
        { h:  15, s: saturation - 25, l: 92 },
        { h:  45, s: saturation - 22, l: 88 },
        { h:   5, s: saturation - 28, l: 90 },
        { h:  25, s: saturation - 24, l: 91 },
        { h: 200, s: saturation - 30, l: 94 },
      ],

      // Minimal — slate monochrome, brand-neutral
      minimal: isDarkMode ? [
        { h: 215, s: saturation - 30, l: 22 },
        { h: 215, s: saturation - 32, l: 26 },
        { h: 215, s: saturation - 28, l: 30 },
        { h: 215, s: saturation - 35, l: 20 },
        { h: 215, s: saturation - 30, l: 32 },
        { h: 215, s: saturation - 33, l: 24 },
      ] : [
        { h: 215, s: saturation - 45, l: 92 },
        { h: 215, s: saturation - 48, l: 88 },
        { h: 215, s: saturation - 42, l: 94 },
        { h: 215, s: saturation - 50, l: 86 },
        { h: 215, s: saturation - 46, l: 90 },
        { h: 215, s: saturation - 44, l: 96 },
      ],
    };
    return palettes[colorScheme] || palettes.default;
  }, [colorScheme, saturation, isDarkMode]);

  // ---------------------------------------------------------------------------
  // Blob init / motion (unchanged structure — only palette changed)
  // ---------------------------------------------------------------------------
  const initBlobs = useCallback((width, height) => {
    const palette = getColorPalette();
    const blobs = [];
    for (let i = 0; i < blobCount; i++) {
      const angle = (i / blobCount) * Math.PI * 2;
      const radiusVariation = 0.3 + Math.random() * 0.4;
      blobs.push({
        x: width / 2 + Math.cos(angle) * width * radiusVariation * 0.3,
        y: height / 2 + Math.sin(angle) * height * radiusVariation * 0.3,
        radius: Math.random() * 350 + 250,
        color: palette[i % palette.length],
        xFreq: (Math.random() * 0.3 + 0.2) * 0.0008,
        yFreq: (Math.random() * 0.3 + 0.2) * 0.0008,
        xPhase: Math.random() * Math.PI * 2,
        yPhase: Math.random() * Math.PI * 2,
        xAmp: width * (0.15 + Math.random() * 0.25),
        yAmp: height * (0.15 + Math.random() * 0.25),
        xCenter: width * (0.15 + (i / blobCount) * 0.7),
        yCenter: height * (0.2 + Math.random() * 0.6),
        pulseFreq: (Math.random() * 0.3 + 0.2) * 0.0015,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseAmp: 0.15 + Math.random() * 0.1,
        opacityBase: 0.7 + Math.random() * 0.3,
      });
    }
    return blobs;
  }, [blobCount, getColorPalette]);

  const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

  const updateBlobs = useCallback((blobs, time) => {
    blobs.forEach(blob => {
      const xProgress = Math.sin(time * blob.xFreq * animationSpeed + blob.xPhase);
      const yProgress = Math.cos(time * blob.yFreq * animationSpeed + blob.yPhase);
      blob.x = blob.xCenter + easeInOutSine((xProgress + 1) / 2) * blob.xAmp * 2 - blob.xAmp;
      blob.y = blob.yCenter + easeInOutSine((yProgress + 1) / 2) * blob.yAmp * 2 - blob.yAmp;
      const pulseProgress = Math.sin(time * blob.pulseFreq * animationSpeed + blob.pulsePhase);
      blob.currentRadius = blob.radius * (1 + pulseProgress * blob.pulseAmp);
      blob.currentOpacity = blob.opacityBase * (0.85 + pulseProgress * 0.15);
    });
  }, [animationSpeed]);

  // ---------------------------------------------------------------------------
  // Draw — switched dark-mode base from `#0a0a0c` to a navy-tinted black
  // (matches the chrome at #0D0E0E / #111114 in PipelineNavigation.css) and
  // softened the additive layer so highlights don't push back to magenta.
  // ---------------------------------------------------------------------------
  const draw = useCallback((ctx, width, height) => {
    // Base — navy-leaning black so the gold/teal blobs read as DataQuad,
    // not as cosmic purple over neutral black.
    ctx.fillStyle = isDarkMode ? '#0a0d11' : '#fafafa';
    ctx.fillRect(0, 0, width, height);

    ctx.globalCompositeOperation = isDarkMode ? 'screen' : 'multiply';

    blobsRef.current.forEach(blob => {
      const gradient = ctx.createRadialGradient(
        blob.x, blob.y, 0,
        blob.x, blob.y, blob.currentRadius
      );
      const { h, s, l } = blob.color;
      const alpha = baseOpacity * blob.currentOpacity;

      gradient.addColorStop(0,    `hsla(${h}, ${s}%, ${l + 10}%, ${alpha})`);
      gradient.addColorStop(0.2,  `hsla(${h}, ${s}%, ${l + 5}%,  ${alpha * 0.85})`);
      gradient.addColorStop(0.4,  `hsla(${h}, ${s}%, ${l}%,      ${alpha * 0.6})`);
      gradient.addColorStop(0.6,  `hsla(${h}, ${s - 5}%, ${l - 5}%,  ${alpha * 0.35})`);
      gradient.addColorStop(0.8,  `hsla(${h}, ${s - 10}%, ${l - 10}%, ${alpha * 0.15})`);
      gradient.addColorStop(1,    `hsla(${h}, ${s - 15}%, ${l - 15}%, 0)`);

      ctx.beginPath();
      ctx.arc(blob.x, blob.y, blob.currentRadius, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
    });

    // Secondary depth pass — softer in dark mode so we don't blow highlights
    // back into purple/magenta territory through hue shifting.
    ctx.globalCompositeOperation = isDarkMode ? 'lighter' : 'overlay';
    ctx.globalAlpha = isDarkMode ? 0.18 : 0.3;

    blobsRef.current.forEach((blob, i) => {
      if (i % 2 === 0) {
        const gradient = ctx.createRadialGradient(
          blob.x + 50, blob.y + 50, 0,
          blob.x + 50, blob.y + 50, blob.currentRadius * 1.5
        );
        const { h, s, l } = blob.color;
        // Smaller hue shift (was +20 / +10) so amber stays amber, teal stays teal —
        // no drift into purple under additive blending.
        gradient.addColorStop(0,   `hsla(${h + 6}, ${s - 10}%, ${l + 12}%, 0.32)`);
        gradient.addColorStop(0.5, `hsla(${h + 3}, ${s - 15}%, ${l + 8}%,  0.12)`);
        gradient.addColorStop(1,   `hsla(${h},     ${s - 20}%, ${l + 4}%,  0)`);
        ctx.beginPath();
        ctx.arc(blob.x + 50, blob.y + 50, blob.currentRadius * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    });

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    addVignette(ctx, width, height, isDarkMode);
  }, [isDarkMode, baseOpacity]);

  const addVignette = (ctx, width, height, isDark) => {
    const gradient = ctx.createRadialGradient(
      width / 2, height / 2, Math.min(width, height) * 0.2,
      width / 2, height / 2, Math.max(width, height) * 0.8
    );
    gradient.addColorStop(0, 'transparent');
    gradient.addColorStop(0.7, 'transparent');
    // Slightly stronger vignette in dark mode so the amber/teal blobs
    // pool toward center and the edges read as deep navy chrome.
    gradient.addColorStop(1, isDark ? 'rgba(0, 0, 0, 0.42)' : 'rgba(255, 255, 255, 0.2)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  };

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = window.innerWidth;
    const height = window.innerHeight;
    timeRef.current += 16;
    updateBlobs(blobsRef.current, timeRef.current);
    draw(ctx, width, height);
    animationRef.current = requestAnimationFrame(animate);
  }, [updateBlobs, draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      blobsRef.current = initBlobs(width, height);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    animate();
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [initBlobs, animate]);

  return (
    <canvas
      ref={canvasRef}
      className={`aurora-background ${className}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 0,
        filter: `blur(${blurAmount}px)`,
        opacity: 0.9,
        ...style
      }}
    />
  );
};

export default AuroraBackground;
